import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import BackToDashboard from "@/components/BackToDashboard";
import DashboardHeader from "@/components/DashboardHeader";
import DashboardFooter from "@/components/DashboardFooter";
import api from "@/lib/api";
import { useMerchant } from "@/lib/useMerchant";
import {
  Webhook as WebhookIcon,
  Plus,
  Trash2,
  Lock,
  ArrowUpRight,
  Copy,
  Check,
  Power,
  Loader2,
  AlertTriangle,
  RotateCw,
  XCircle,
} from "lucide-react";

/**
 * Webhooks.jsx
 * ------------------------------------------------------------------
 * Pro-only page for merchants (mainly agencies) to push order/return
 * events to their own systems in real time.
 *
 * Wired to:
 *   GET    /webhooks          -> { webhooks: [...] }
 *   POST   /webhooks          -> { webhook, secret }   (secret shown ONCE)
 *   POST   /webhooks/toggle   -> { webhook }
 *   DELETE /webhooks?id=      -> { deleted: true }
 *
 * Backend also enforces the Pro gate (403 pro_plan_required), so a
 * direct API call can't bypass it — this page's lock screen is just UX.
 * ------------------------------------------------------------------
 */

const EVENTS = [
  { key: "order.created", label: "Order created" },
  { key: "order.paid", label: "Order paid" },
  { key: "order.shipped", label: "Order shipped" },
  { key: "return.requested", label: "Return requested" },
  { key: "return.approved", label: "Return approved" },
  { key: "return.rejected", label: "Return rejected" },
];

function EventBadge({ event }) {
  return (
    <span className="rounded-full bg-ink/5 px-2.5 py-1 text-[11px] font-semibold text-ink/70">
      {event}
    </span>
  );
}

export default function Webhooks() {
  const { me, loading: meLoading } = useMerchant();
  const isPro = me?.plan === "pro";

  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [targetUrl, setTargetUrl] = useState("");
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newSecret, setNewSecret] = useState(null); // shown once after create
  const [copied, setCopied] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [failed, setFailed] = useState([]);
  const [failedLoading, setFailedLoading] = useState(true);
  const [resendingId, setResendingId] = useState(null);

  const loadWebhooks = () =>
    api
      .get("/webhooks")
      .then(({ data }) => setWebhooks(data.webhooks || []))
      .catch(() => toast.error("Couldn't load your webhooks. Please try again."))
      .finally(() => setLoading(false));

  const loadFailed = () =>
    api
      .get("/webhooks/failed")
      .then(({ data }) => setFailed(data.deliveries || []))
      .catch(() => {})
      .finally(() => setFailedLoading(false));

  useEffect(() => {
    if (!isPro) {
      setLoading(false);
      setFailedLoading(false);
      return;
    }
    loadWebhooks();
    loadFailed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPro]);

  const handleResend = async (logId) => {
    setResendingId(logId);
    try {
      const { data } = await api.post("/webhooks/resend", { log_id: logId });
      if (data.delivered) {
        toast.success("Delivered — the event went through.");
        setFailed((prev) => prev.filter((f) => f.log_id !== logId));
      } else {
        toast.error(`Still failing (status ${data.response_code ?? "—"}). Check the endpoint is up.`);
        loadFailed();
      }
    } catch {
      toast.error("Couldn't resend that event. Try again.");
    } finally {
      setResendingId(null);
    }
  };

  const toggleEvent = (key) => {
    setSelectedEvents((prev) => (prev.includes(key) ? prev.filter((e) => e !== key) : [...prev, key]));
  };

  const handleCreate = async () => {
    if (!/^https:\/\//.test(targetUrl.trim())) {
      toast.error("Webhook URL must start with https://");
      return;
    }
    if (selectedEvents.length === 0) {
      toast.error("Pick at least one event.");
      return;
    }
    if (webhooks.length >= 10) {
      toast.error("You've reached the limit of 10 webhooks. Remove one to add another.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/webhooks", { target_url: targetUrl.trim(), events: selectedEvents });
      setNewSecret(data.secret);
      setTargetUrl("");
      setSelectedEvents([]);
      setAddOpen(false);
      loadWebhooks();
    } catch (err) {
      if (err?.response?.status === 403) toast.error("Webhooks require the Pro plan.");
      else if (err?.response?.status === 409) toast.error("You've reached the limit of 10 webhooks. Remove one to add another.");
      else toast.error("Couldn't add that webhook. Check the URL and try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id) => {
    setBusyId(id);
    try {
      const { data } = await api.post("/webhooks/toggle", { id });
      setWebhooks((prev) => prev.map((w) => (w.id === data.webhook.id ? { ...w, active: data.webhook.active } : w)));
    } catch {
      toast.error("Couldn't update that webhook. Try again.");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id) => {
    setBusyId(id);
    try {
      await api.delete(`/webhooks?id=${id}`);
      setWebhooks((prev) => prev.filter((w) => w.id !== id));
      toast.success("Webhook removed.");
    } catch {
      toast.error("Couldn't remove that webhook. Try again.");
    } finally {
      setBusyId(null);
      setDeleteTarget(null);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(newSecret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (meLoading) {
    return (
      <div className="min-h-screen bg-cream">
        <DashboardHeader me={me} />
        <section className="grid h-64 place-items-center">
          <Loader2 className="animate-spin text-ink/40" size={28} />
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <DashboardHeader me={me} />
      <section className="px-6 py-12 md:px-14 md:py-16">
      <div className="mx-auto max-w-4xl">
        <BackToDashboard />
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-hotpink">Integrations</p>
        <h1 className="mt-3 text-4xl font-black tracking-tight text-ink md:text-6xl">
          Webhooks
        </h1>
        <p className="mt-4 max-w-xl text-ink/60">
          Push order and return events to your own CRM, ERP, or automation stack in real time —
          no polling, signed payloads you can verify.
        </p>

        {!isPro ? (
          <div className="mt-10 rounded-[2rem] border border-ink/10 bg-white p-8 md:p-10" data-testid="webhooks-locked">
            <div className="flex items-start gap-4">
              <span className="grid h-14 w-14 flex-none place-items-center rounded-3xl bg-ink/5 text-ink/60">
                <Lock size={22} />
              </span>
              <div className="flex-1">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Webhooks</p>
                <h3 className="mt-1 font-display text-2xl font-black tracking-tight">
                  Webhooks are a Pro feature
                </h3>
                <p className="mt-2 max-w-xl text-sm text-ink/60">
                  Agencies and teams running multiple systems can wire orders and returns straight
                  into their own software — upgrade to Pro to turn it on.
                </p>
                <a
                  href="/plans"
                  data-testid="webhooks-upgrade-cta"
                  className="cta-glow mt-5 inline-flex items-center gap-2 rounded-full bg-sunset-gradient px-5 py-2.5 text-sm font-semibold text-white"
                >
                  Upgrade to Pro <ArrowUpRight size={14} />
                </a>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Add webhook */}
            <div className="mt-10 overflow-hidden rounded-3xl border border-ink/10 bg-white">
              <div className="flex flex-col gap-3 border-b border-ink/10 px-6 py-5 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 place-items-center rounded-2xl bg-mint text-ink">
                    <WebhookIcon size={18} />
                  </span>
                  <h2 className="font-display text-lg font-black tracking-tight">Your webhooks</h2>
                  <span className="text-xs text-ink/40">{webhooks.length}/10</span>
                </div>
                {!addOpen && (
                  <button
                    onClick={() => setAddOpen(true)}
                    disabled={webhooks.length >= 10}
                    className="inline-flex items-center gap-2 rounded-full bg-ink px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Plus size={15} /> {webhooks.length >= 10 ? "Limit reached" : "Add webhook"}
                  </button>
                )}
              </div>

              {addOpen && (
                <div className="border-b border-ink/10 bg-ink/[0.02] px-6 py-6">
                  <label className="text-xs font-semibold uppercase tracking-wide text-ink/50">
                    Endpoint URL (https only)
                  </label>
                  <input
                    type="url"
                    value={targetUrl}
                    onChange={(e) => setTargetUrl(e.target.value)}
                    placeholder="https://yourcrm.example.com/checkoutdm-hook"
                    className="mt-1.5 w-full rounded-xl border border-ink/10 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
                  />
                  <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-ink/50">Events</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {EVENTS.map((ev) => (
                      <button
                        key={ev.key}
                        onClick={() => toggleEvent(ev.key)}
                        className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                          selectedEvents.includes(ev.key)
                            ? "border-hotpink bg-hotpink text-white"
                            : "border-ink/15 bg-white text-ink/70"
                        }`}
                      >
                        {ev.label}
                      </button>
                    ))}
                  </div>
                  <div className="mt-5 flex items-center gap-3">
                    <button
                      onClick={handleCreate}
                      disabled={saving}
                      className="inline-flex items-center gap-2 rounded-full bg-sunset-gradient px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
                    >
                      {saving && <Loader2 size={14} className="animate-spin" />} Create webhook
                    </button>
                    <button
                      onClick={() => {
                        setAddOpen(false);
                        setTargetUrl("");
                        setSelectedEvents([]);
                      }}
                      className="text-sm font-semibold text-ink/50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* List */}
              {loading ? (
                <div className="px-6 py-10 text-center text-sm text-ink/50">Loading…</div>
              ) : webhooks.length === 0 ? (
                <div className="px-6 py-10 text-center text-sm text-ink/50">
                  No webhooks yet — add one above to start receiving events.
                </div>
              ) : (
                <ul className="divide-y divide-ink/10">
                  {webhooks.map((w) => (
                    <li key={w.id} className="flex flex-col gap-3 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-ink">{w.target_url}</p>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {w.events.map((e) => (
                            <EventBadge key={e} event={e} />
                          ))}
                        </div>
                        <p className="mt-2 text-xs text-ink/40">
                          Secret ends in •••{w.secret_last4} · added{" "}
                          {new Date(w.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                        </p>
                      </div>
                      <div className="flex flex-none items-center gap-2">
                        <span
                          className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                            w.active ? "bg-mint text-ink" : "bg-ink/10 text-ink/50"
                          }`}
                        >
                          {w.active ? "Active" : "Paused"}
                        </span>
                        <button
                          onClick={() => handleToggle(w.id)}
                          disabled={busyId === w.id}
                          title={w.active ? "Pause" : "Resume"}
                          className="grid h-9 w-9 place-items-center rounded-xl border border-ink/10 text-ink/60 hover:bg-ink/5 disabled:opacity-50"
                        >
                          <Power size={15} />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(w)}
                          disabled={busyId === w.id}
                          title="Delete"
                          className="grid h-9 w-9 place-items-center rounded-xl border border-ink/10 text-ink/60 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <p className="mt-4 text-xs text-ink/40">
              Every payload is signed — verify it with HMAC-SHA256 using your webhook's secret
              against the <code className="rounded bg-ink/5 px-1 py-0.5">X-CheckoutDM-Signature</code> header.{" "}
              <Link to="/webhooks-guide" className="font-semibold text-hotpink hover:underline">
                Read the integration guide →
              </Link>
            </p>

            {/* Failed deliveries — grouped per webhook, so a merchant can see exactly which one is broken */}
            {!failedLoading && failed.length > 0 && (
              <div className="mt-6 overflow-hidden rounded-3xl border border-hotpink/20 bg-white">
                <div className="flex items-center gap-3 border-b border-hotpink/10 bg-hotpink/5 px-6 py-4">
                  <span className="grid h-9 w-9 flex-none place-items-center rounded-2xl bg-hotpink/10 text-hotpink">
                    <AlertTriangle size={16} />
                  </span>
                  <div>
                    <h2 className="font-display text-base font-black tracking-tight">Failed deliveries</h2>
                    <p className="text-xs text-ink/50">
                      Grouped by webhook — resend once the specific endpoint is back up.
                    </p>
                  </div>
                </div>
                {Object.entries(
                  failed.reduce((groups, f) => {
                    const key = f.subscription_id ?? f.target_url;
                    (groups[key] ||= { target_url: f.target_url, items: [] }).items.push(f);
                    return groups;
                  }, {})
                ).map(([subId, group]) => (
                  <div key={subId} className="border-b border-ink/10 last:border-b-0">
                    <p className="truncate bg-ink/[0.02] px-6 py-2.5 text-xs font-semibold text-ink/60">
                      {group.target_url} <span className="font-normal text-ink/40">· {group.items.length} failing</span>
                    </p>
                    <ul className="divide-y divide-ink/10">
                      {group.items.map((f) => (
                        <li key={f.log_id} className="flex flex-col gap-2 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <EventBadge event={f.event_type} />
                              {f.status === "dead" ? (
                                <span className="inline-flex items-center gap-1 rounded-full bg-ink/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-ink/60">
                                  <XCircle size={10} /> Stopped retrying
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 rounded-full bg-peach/40 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-ink/60">
                                  Retrying · attempt {f.attempt_count}
                                </span>
                              )}
                            </div>
                            <p className="mt-1.5 text-xs text-ink/50">
                              response {f.response_code ?? "no response"} ·{" "}
                              {new Date(f.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                            </p>
                          </div>
                          <button
                            onClick={() => handleResend(f.log_id)}
                            disabled={resendingId === f.log_id}
                            className="inline-flex flex-none items-center gap-1.5 rounded-full border border-ink/15 px-3.5 py-1.5 text-xs font-semibold text-ink/70 hover:bg-ink/5 disabled:opacity-50"
                          >
                            {resendingId === f.log_id ? (
                              <Loader2 size={12} className="animate-spin" />
                            ) : (
                              <RotateCw size={12} />
                            )}
                            Resend
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Secret shown once, right after creation */}
      {newSecret && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-ink/40 px-6" onClick={() => setNewSecret(null)}>
          <div
            className="w-full max-w-md rounded-3xl bg-white p-7"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 flex-none place-items-center rounded-2xl bg-peach text-ink">
                <AlertTriangle size={18} />
              </span>
              <div>
                <h3 className="font-display text-lg font-black tracking-tight">Save this secret now</h3>
                <p className="mt-1 text-sm text-ink/60">
                  This is shown only once. You'll need it to verify incoming webhook signatures.
                </p>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2 rounded-xl border border-ink/10 bg-ink/[0.03] px-4 py-3">
              <code className="flex-1 truncate text-sm font-mono">{newSecret}</code>
              <button onClick={copySecret} className="grid h-8 w-8 flex-none place-items-center rounded-lg bg-white text-ink/60">
                {copied ? <Check size={15} className="text-mint" /> : <Copy size={15} />}
              </button>
            </div>
            <button
              onClick={() => setNewSecret(null)}
              className="mt-5 w-full rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-white"
            >
              I've saved it
            </button>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-ink/40 px-6" onClick={() => setDeleteTarget(null)}>
          <div className="w-full max-w-sm rounded-3xl bg-white p-7" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-lg font-black tracking-tight">Remove this webhook?</h3>
            <p className="mt-1 text-sm text-ink/60 truncate">{deleteTarget.target_url}</p>
            <p className="mt-2 text-xs text-ink/50">This stops all events to this URL immediately.</p>
            <div className="mt-5 flex gap-3">
              <button
                onClick={() => handleDelete(deleteTarget.id)}
                className="flex-1 rounded-full bg-red-600 px-4 py-2.5 text-sm font-semibold text-white"
              >
                Remove
              </button>
              <button onClick={() => setDeleteTarget(null)} className="flex-1 rounded-full border border-ink/15 px-4 py-2.5 text-sm font-semibold text-ink/70">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
    <DashboardFooter />
    </div>
  );
}
